import { DateTime, Settings } from "luxon";

/**
 * Fuso do SO, lido uma vez. `Intl.DateTimeFormat().resolvedOptions()` constrói
 * um formatter novo a cada chamada e custa ~30µs — mais de 85% do custo total
 * de `getDateTimeFromJSDate`, que os pipes `intl*` (impuros) invocam a cada
 * ciclo de change detection, por binding. O valor não muda a meio da sessão, e
 * o `DateTimeService.systemZone` já o cacheia pela mesma razão.
 *
 * Preguiçoso e não uma constante de módulo para o valor não ficar preso no
 * momento do import: um teste que fixe `process.env.TZ` antes do primeiro uso
 * continua a ser respeitado.
 */
let cachedSOZone: string | null = null;
const getSOZone = (): string => {
    cachedSOZone ??= Intl.DateTimeFormat().resolvedOptions().timeZone;
    return cachedSOZone;
}

/**
 * Get a JavaScript Date object from a date reference, preserving the local time.
 *
 * Accepts anything `getDateTime` accepts — ISO 8601 string, `DateTime`, `Date` or
 * epoch millis. There is no "now" overload: pass `DateTime.now()` explicitly, whose
 * clock `SERVER_CLOCK_INITIALIZER` binds to the synced time so it does not inherit a
 * wrong machine clock. Use this — rather than a bare `DateTime.now().toJSDate()` —
 * wherever a real `Date` has to be handed to DevExtreme (filter expressions,
 * `dxDateBox` values), since those compare against grid values built by this
 * very function.
 *
 * @param date date reference (ISO string, `DateTime`, `Date` or epoch millis)
 * @returns JavaScript Date object, or `null` when `date` is `null`/`undefined`/`''`,
 *          de um tipo que `getDateTime` não sabe ler, ou de um valor que não faz parse
 */
export const getJSDate = (date: any): Date | null => {
    if (isEmptyValue(date)) {
        return null;
    }

    const SOZone = getSOZone();
    // `getDateTime` devolve `null` para qualquer coisa que não seja string,
    // number, `Date` ou `DateTime`, e um `DateTime` INVÁLIDO para um valor que
    // não faz parse. Sem estas duas guardas o primeiro caso lançava
    // `TypeError` e o segundo devolvia um `Date` `Invalid Date` — os dois
    // contra o `null` que a assinatura promete. O parâmetro é `any`, portanto
    // não há tipo a impedir nenhum dos dois.
    const dateTime = getDateTime(date)?.setZone(SOZone, { keepLocalTime: true });

    return dateTime?.isValid ? dateTime.toJSDate() : null;
}

/**
 * Lê um `Date` e devolve-o como `DateTime` no fuso da conta, PRESERVANDO os
 * dígitos — não convertendo o instante.
 *
 * ## Leia isto antes de "corrigir" esta função
 *
 * A tentação é achar que um `Date` vindo de uma grelha é um instante UTC e que
 * portanto se deve ler com `{ zone: 'utc' }`. **Não é, e não se deve.** Já houve
 * um bug e um code review inteiro perdidos nessa suposição.
 *
 * O `ODataStore` do DevExtreme não desserializa a string da resposta como
 * instante. O `parseISO8601` dele (`devextreme/.../data/odata/m_utils.js`)
 * **descarta o `Z`** e escreve os dígitos com setters LOCAIS (`setFullYear`,
 * `setHours`, ...). O `Date` que sai de lá tem, no relógio de parede DA MÁQUINA,
 * exatamente os dígitos que o servidor mandou:
 *
 * ```text
 * servidor: "2026-04-01T00:00:00Z"
 * new Date do DevExtreme, lido na máquina (Tóquio): 2026-04-01 00:00
 * o mesmo Date, lido em UTC:                        2026-03-31 15:00  ← não é isto que se quer
 * ```
 *
 * Por isso esta função lê na zona do SO (`getSOZone()`): é a única leitura que
 * recupera os dígitos originais. Depois troca só o RÓTULO do fuso para o da
 * conta (`keepLocalTime: true`), sem mexer nos dígitos.
 *
 * ## A propriedade que isto garante
 *
 * As duas dependências da máquina — a do DevExtreme a escrever e a desta função
 * a ler — cancelam-se. O que sai é o dígito do servidor, **seja qual for o fuso
 * do SO**. Foi assim medido, com a conta em `America/Sao_Paulo` e um `DueOn` de
 * 01/04:
 *
 * ```text
 * máquina em São Paulo    -> 01/04  ✔
 * máquina em Tóquio       -> 01/04  ✔
 * máquina em Los Angeles  -> 01/04  ✔
 * ```
 *
 * Uma versão que lesse em UTC, ou que reancorasse o valor no fuso da conta,
 * devolvia 01/04 12:00 em Tóquio e 31/03 20:00 em Los Angeles.
 *
 * ## Quem já é instante de verdade
 *
 * As colunas `*Utc` — essas SIM carregam um instante — são convertidas antes de
 * chegarem aqui, pelo `formatUtcDatesInData` do `DxOdataStoreService` (caminho
 * DevExtreme) ou pelo `DateInterceptor` (caminho `HttpClient`). Quando chegam a
 * esta função já estão em hora de parede da conta, como todas as outras. **O
 * nome da coluna é o contrato**: sem `Utc` no nome, é hora de parede.
 *
 * @param date `Date` a ler; tipicamente vindo de uma grelha DevExtreme
 * @returns o mesmo relógio de parede, rotulado no fuso da conta
 * @see getJSDate — o inverso, para devolver um `Date` ao DevExtreme
 */
export const getDateTimeFromJSDate = (date: Date): DateTime => {
    // `Settings.defaultZone.name` e não `DateTime.now().zoneName`: é o mesmo
    // valor — o segundo resolve-se para o primeiro — mas sem construir um
    // `DateTime` só para o ler (~3.9µs vs ~0.04µs). Continua a acompanhar o
    // `LUXON_ZONE_INITIALIZER`, que reescreve `Settings.defaultZone` sempre que
    // o fuso da conta muda; cacheá-lo congelaria o fuso no valor de arranque.
    return DateTime.fromJSDate(date, { zone: getSOZone() })
        .setZone(Settings.defaultZone.name, { keepLocalTime: true });
}

const isEmptyValue = (date: Date | string | number | null): boolean => {
    if (date === null || date === undefined || date === '') {
        return true;
    }
    return false;
}

/**
 * Ponto de entrada único para transformar qualquer referência de data num
 * `DateTime` no fuso da conta. Aceita `DateTime`, `Date`, epoch millis e string
 * ISO.
 *
 * Os dois ramos que importam tratam o valor pela mesma regra, por caminhos
 * diferentes — em ambos os casos **os dígitos mandam, o sufixo não**:
 *
 * - **`Date`** (tipicamente de uma grelha DevExtreme) → {@link
 *   getDateTimeFromJSDate}. Leia o JSDoc de lá antes de mexer em qualquer coisa
 *   nesta cadeia: explica por que o `Date` NÃO é um instante UTC.
 * - **string** → o `Z`/offset é descartado pelo {@link cleanDateStr} e os
 *   dígitos são lidos no `Settings.defaultZone` (o fuso da conta, ligado pelo
 *   `LUXON_ZONE_INITIALIZER`). O `Z` que as strings desta app carregam é
 *   decoração desde o `LUXON_ISO_INITIALIZER` — não quer dizer que o valor
 *   esteja em UTC.
 *
 * A conversão UTC ↔ local acontece **só na fronteira com o backend**
 * (`DateInterceptor` e `formatUtcDatesInData`), nunca aqui. Reconverter neste
 * ponto desloca o valor pelo offset da conta — é o erro clássico nesta base de
 * código.
 *
 * @param reference `DateTime`, `Date`, epoch millis ou string ISO
 * @returns `DateTime` no fuso da conta, ou `null` para vazio/tipo não suportado
 */
export const getDateTime = (reference: any): DateTime | null => {
  if (isEmptyValue(reference)) {
    return null;
  }

  if (reference instanceof DateTime) {
    return reference;
  }

  if (reference instanceof Date) {
    return getDateTimeFromJSDate(reference);
  } 
  
  if (typeof reference === 'number') {
    return DateTime.fromMillis(reference);
  }
  
  if (typeof reference === 'string') {
    return DateTime.fromISO(cleanDateStr(reference.trim()));
  } 
  
  return null;
}

export const cleanDateStr = (date: string): string => {
  // (/Z$|[+-]\d{2}:\d{2}$/, '') Strip Z or offset
  // (/(\.\d{3})\d+/, '$1') Truncate to 3 decimal places for Luxon
  return date?.replace(/Z$|[+-]\d{2}:\d{2}$/, '')?.replace(/(\.\d{3})\d+/, '$1') 
}

/**
 * Maior instante que o `Intl` aceita formatar, em millis — o limite do tipo
 * `Date` do ECMAScript (±100.000.000 dias em torno da época).
 */
export const MAX_TIME_VALUE_MS = 8.64e15;

/**
 * Se o `Intl` consegue formatar este instante.
 *
 * `Intl.DateTimeFormat.prototype.format` e `formatToParts` **lançam**
 * `RangeError: Invalid time value` para `NaN`, `±Infinity` e para qualquer
 * finito acima de {@link MAX_TIME_VALUE_MS} — não devolvem uma string vazia nem
 * `Invalid Date` como o `toLocaleString` do Luxon. É por isso que um "agora"
 * corrompido rebenta em silêncio por toda a app menos no `Intl`, que é o único
 * participante estrito (Sentry THE-NEW-130: `DateTime.now().toMillis()` chegou
 * `NaN` ao `Intl` e derrubou a máscara de TODOS os inputs de data).
 *
 * Use isto para guardar a fronteira de qualquer valor que vá alimentar o `Intl`
 * e cuja origem não seja garantidamente sã — o relógio da máquina, à cabeça.
 */
export const isFormattableInstant = (ms: number): boolean =>
  Number.isFinite(ms) && Math.abs(ms) <= MAX_TIME_VALUE_MS;
